import React, { useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useSearchTourOffers } from '~/hooks/apis/tours/useTourOffers';
import useDebounced from '~/hooks/useDebounced';

import { TourOfferRelatedTour } from '~/types/tours/tourOffer';

interface Props {
  label: string;
  value: TourOfferRelatedTour | null;
  onChange: (tour: TourOfferRelatedTour | null) => void;
  /** Tours that can't be picked: this one, and any already in the list. */
  excludeIds: Array<string>;
  helperText?: string;
  /** Clears itself after a pick, for adding to a list. */
  clearOnPick?: boolean;
}

/** Search tour offers by name, tour offer id or legacy tour id. */
export default function NativeTourOfferPicker({
  label,
  value,
  onChange,
  excludeIds,
  helperText = 'Search by name, tour offer id or legacy tour id',
  clearOnPick = false,
}: Props) {
  const [inputValue, setInputValue] = useState('');
  const search = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);
  const { data: results = [], isFetching } = useSearchTourOffers(search, false);

  const options = useMemo<Array<TourOfferRelatedTour>>(() => {
    const found = results
      .filter((offer) => !excludeIds.includes(offer.id))
      .map((offer) => ({ tourOfferId: offer.id, legacyTourId: offer.legacyTourId, name: offer.name }));
    return value && !found.some((option) => option.tourOfferId === value.tourOfferId) ? [value, ...found] : found;
  }, [results, excludeIds, value]);

  return (
    <Autocomplete
      value={clearOnPick ? null : value}
      onChange={(_event, tour) => {
        onChange(tour);
        if (clearOnPick) {
          setInputValue('');
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={options}
      getOptionLabel={(option) => option.name ?? option.tourOfferId}
      isOptionEqualToValue={(option, selected) => option.tourOfferId === selected.tourOfferId}
      filterOptions={(all) => all}
      loading={isFetching}
      noOptionsText={search ? `No tours match "${search}"` : 'Type to search'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
